
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUserProgress } from "@/hooks/use-user-progress";
import { Flame, Star, Target, TrendingUp, Zap } from "lucide-react";

/**
 * UserProgressSummary Component
 * 
 * Displays the user's learning progress in a gamified way including:
 * - Completion stats (topics/lessons unlocked)
 * - Current learning streak with fire emoji
 * - Today's XP earned
 * - Motivational messaging
 * 
 * This component makes the learning experience feel more like a game
 * and encourages continued engagement.
 */
export const UserProgressSummary = () => {
  const { data: userProgress, isLoading, error } = useUserProgress();

  // Show loading state with skeleton
  if (isLoading) {
    return (
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-400 to-orange-400 rounded-[2rem] opacity-10 blur-2xl"></div>
        <div className="relative bg-background/70 backdrop-blur-xl border border-background/30 rounded-[2rem] p-6 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-32"></div>
              <div className="h-6 bg-gray-200 rounded w-48"></div>
            </div>
            <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    console.error("❌ Error loading user progress:", error);
    return null; // Fail gracefully, don't break the page
  }

  // Handle no user/no progress state  
  if (!userProgress) {
    return (
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-green-400 via-blue-400 to-purple-400 rounded-[2rem] opacity-15 blur-2xl"></div>
        <div className="relative bg-background/80 backdrop-blur-xl border border-background/30 rounded-[2rem] p-6 shadow-lg">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="h-8 w-8 text-yellow-500 animate-pulse" />
              <h3 className="text-2xl font-black bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                Ready to Start Your Journey?
              </h3>
            </div>
            <p className="text-gray-600 font-medium mb-4">
              Begin your Centse adventure and start earning XP today! 🚀
            </p>
            <Badge className="bg-gradient-to-r from-green-500 to-blue-500 text-white">
              New Learner 🌱
            </Badge>
          </div>
        </div>
      </div>
    );
  }

  const {
    completedLessons,
    completedTopics,
    todayXP,
    currentStreak,
    totalXP
  } = userProgress;

  return (
    <div className="relative">
      {/* Multiple gradient background blobs for visual interest */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-400 to-orange-400 rounded-[2rem] opacity-15 blur-2xl"></div>
      <div className="absolute inset-0 bg-gradient-to-tl from-green-400 via-blue-400 to-purple-400 rounded-[2rem] opacity-10 blur-xl"></div>
      
      <div className="relative bg-background/80 backdrop-blur-xl border border-background/30 rounded-[2rem] p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
        <div className="flex items-center justify-between">
          {/* Progress Stats */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-bold text-gray-900">Your Learning Journey</h3>
            </div>
            
            <div className="flex items-center gap-4 flex-wrap">
              {/* Topics Unlocked */}
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {completedTopics} Topics
                </Badge>
              </div>

              {/* Lessons Completed */}
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                  📚 {completedLessons} Lessons
                </Badge>
              </div>

              {/* Today's XP */}
              {todayXP > 0 && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                    <Zap className="h-3 w-3 mr-1" />
                    +{todayXP} XP today
                  </Badge>
                </div>
              )}

              {/* Learning Streak */}
              {currentStreak > 0 && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-gradient-to-r from-red-500 to-orange-500 text-white">
                    <Flame className="h-3 w-3 mr-1 animate-pulse" />
                    {currentStreak}-day streak
                  </Badge>
                </div>
              )}
            </div>

            {/* Motivational message */}
            <p className="text-sm text-gray-600 font-medium">
              {currentStreak > 0 
                ? `🔥 You're on fire! Keep that ${currentStreak}-day streak going!`
                : completedLessons > 0
                ? "💪 Great progress! Ready for your next lesson?"
                : "🚀 Start your learning journey today!"
              }
            </p>
          </div>

          {/* XP Display */}
          <div className="text-center">
            <div className="p-4 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-2xl shadow-lg text-white">
              <Star className="h-6 w-6 mx-auto mb-1 animate-pulse" />
              <div className="text-2xl font-black">{totalXP}</div>
              <div className="text-xs font-bold opacity-90">Total XP</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
